import React from 'react';
import styled from '@emotion/styled';
import { colors } from 'src/theme';
import { CheckboxInput, Flex, Tag, Text } from 'src/components';

export interface CheckboxOptionGroupOption {
  id: string;
  label: string;
  description?: string;
  isPopular?: boolean;
}

export interface CheckboxOptionGroupProps {
  options: CheckboxOptionGroupOption[];
  value?: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const SectionLabel = styled.label({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  color: colors.text1,
  display: 'block',
  marginBottom: 0,
});

const ValidationError = styled.div({
  fontSize: '14px',
  color: colors.error1,
  paddingTop: '4px',
});

const OptionRow = styled.div<{ disabled?: boolean }>(({ disabled }) => ({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

export const CheckboxOptionGroup: React.FC<CheckboxOptionGroupProps> = ({
  options,
  value = [],
  onChange,
  label,
  error,
  disabled = false,
  className,
}) => {
  const selectedSet = new Set(value);

  const handleToggle = (id: string) => {
    if (disabled) return;
    const next = selectedSet.has(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next);
  };

  return (
    <Flex vertical gap={16} style={{ width: '100%' }} className={className}>
      {label != null && label !== '' && <SectionLabel>{label}</SectionLabel>}
      {options.map((option) => (
        <OptionRow
          key={option.id}
          disabled={disabled}
          onClick={() => !disabled && handleToggle(option.id)}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle(option.id);
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          <CheckboxInput
            value={selectedSet.has(option.id)}
            // onChange={() => handleToggle(option.id)}
            left
            disabled={disabled}
            labelComponent={null}
          />
          <Flex vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Flex gap={8} align="center" wrap="wrap" style={{ width: '100%' }}>
              <Text type="base" bold color={colors.text2}>
                {option.label}
              </Text>
              {option.isPopular && <Tag background={colors.success2} value="POPULARNO" bold />}
            </Flex>
            {option.description != null && option.description !== '' && (
              <Text type="tiny" color={colors.text3}>
                {option.description}
              </Text>
            )}
          </Flex>
        </OptionRow>
      ))}
      {error != null && error !== '' && <ValidationError>{error}</ValidationError>}
    </Flex>
  );
};
